import { isDisposalType, GeoCoord, PipelineGraphEdgeEntity, PipelineGraphNodeEntity, siteType, SiteType,} from '@ots-frontend/ots-data'
import {IEdge, INode} from 'react-digraph';
import {v4 as uuid} from 'uuid';

export const PIPELINE_GRAPH_NODE_KEY = 'id';

const EmptyShape = (
    <symbol viewBox="0 0 100 100" id="empty" key="0">
        <circle cx="50" cy="50" r="45"></circle>
    </symbol>
);

const WellShape = (
    <symbol viewBox="0 0 75 75" id="well" key="1" width="75" height="75">
        <rect x="0" y="0" rx="2" ry="2" width="75" height="75"/>
    </symbol>
);

const DisposalShape = (
    <symbol viewBox="-27 0 154 154" id="disposal" width="154" height="154">
        <rect transform="translate(50) rotate(45)" width="109" height="109"/>
    </symbol>
);

export const PipelineGraphConfig = {
    NodeTypes: {
        empty: {
            typeText: 'Site',
            shapeId: '#empty',
            shape: EmptyShape,
        },
        well: {
            typeText: 'Well',
            shapeId: '#well',
            shape: WellShape,
        },
        disposal: {
            typeText: 'Disposal',
            shapeId: '#disposal',
            shape: DisposalShape,
        },
    },
    NodeSubtypes: {},
    EdgeTypes: {
        emptyEdge: {
            // required to show empty edges
            shapeId: '#emptyEdge',
            shape: (
                <symbol viewBox="0 0 50 50" id="emptyEdge" key="0">
                    <circle cx="25" cy="25" r="8" fill="currentColor"></circle>
                </symbol>
            ),
        },
    },
};

export type PipelineGraphViewNode = {
    id: string;
    objectId: string;
    objectType: string;
    objectName?: string;
    siteType?: SiteType;
    billable?: boolean;
    comments?: string;
};

export type PipelineGraphViewEdge = {
    id: string;
    fromNodeId: string;
    toNodeId: string;
    geoData?: GeoCoord[];
};

export type PipelineGraphViewData = {
    nodes: PipelineGraphViewNode[];
    edges: PipelineGraphViewEdge[];
};

export type RawPipelineGraphViewData = {
    nodes: INode[];
    edges: IEdge[];
};

export const toPipelineGraphViewNodes = (
    graphNodes: PipelineGraphNodeEntity[] | undefined
): PipelineGraphViewNode[] => {
    return (
        graphNodes?.map((n) => ({
            id: n.id,
            objectId: n.objectId,
            objectType: n.objectType,
            objectName: n.objectName,
            siteType: n.siteType,
            billable: n.billable,
            comments: n.comments,
        })) ?? []
    );
};

export const toPipelineGraphViewEdges = (
    graphEdges: PipelineGraphEdgeEntity[] | undefined,
    copy = true
): PipelineGraphViewEdge[] => {
    return (
        graphEdges?.map((e) => ({
            id: copy ? e.id : uuid(),
            fromNodeId: e.fromPipelineNodeId,
            toNodeId: e.toPipelineNodeId,
            geoData: e.geoData ? [...e.geoData] : undefined,
        })) ?? []
    );
};

const toNodeType = (node: PipelineGraphViewNode): string => {
    if (node.objectType === 'WELL') {
        return 'well';
    } else if (node.objectType === 'SITE') {
        if (isDisposalType(node.siteType)) {
            return 'disposal';
        }
    }

    return 'empty';
};

export const toGraphViewNodes = (
    graphNodes: PipelineGraphViewNode[] | undefined
): INode[] => {
    return (
        graphNodes?.map((n) => ({
            id: n.id,
            title: n.objectName ?? 'Unknown',
            type: toNodeType(n),
            data: {
                id: n.id,
                objectId: n.objectId,
                objectType: n.objectType,
                objectName: n.objectName,
                siteType: n.siteType,
                billable: n.billable,
                comments: n.comments,
            },
        })) ?? []
    );
};

export const toGraphViewEdges = (
    graphEdges: PipelineGraphViewEdge[] | undefined
): IEdge[] => {
    return (
        graphEdges?.map((e) => ({
            id: e.id,
            source: e.fromNodeId,
            target: e.toNodeId,
            type: 'emptyEdge',
            data: {
                geoData: e.geoData,
            },
        })) ?? []
    );
};

export const copyPipelineGraphViewData = (
    graphNodes: PipelineGraphNodeEntity[] | undefined,
    graphEdges: PipelineGraphEdgeEntity[] | undefined,
    copyId = false
): PipelineGraphViewData => {
    const nodeIdMap = new Map<string, string>();

    const viewNodes: PipelineGraphViewNode[] = [];
    const viewEdges: PipelineGraphViewEdge[] = [];

    if (graphNodes) {
        for (const graphNode of graphNodes) {
            const newId = copyId ? graphNode.id : uuid();
            nodeIdMap.set(graphNode.id, newId);

            const viewNode: PipelineGraphViewNode = {
                id: newId,
                objectId: graphNode.objectId,
                objectType: graphNode.objectType,
                objectName: graphNode.objectName,
                siteType: graphNode.siteType,
                billable: graphNode.billable,
                comments: graphNode.comments,
            };

            viewNodes.push(viewNode);
        }
    }

    if (graphEdges) {
        for (const graphEdge of graphEdges) {
            const fromNodeId = nodeIdMap.get(graphEdge.fromPipelineNodeId);
            const toNodeId = nodeIdMap.get(graphEdge.toPipelineNodeId);

            if (fromNodeId && toNodeId) {
                const viewEdge: PipelineGraphViewEdge = {
                    id: copyId ? graphEdge.id : uuid(),
                    fromNodeId: fromNodeId,
                    toNodeId: toNodeId,
                    geoData: graphEdge.geoData ? [...graphEdge.geoData] : undefined,
                };

                viewEdges.push(viewEdge);
            }
        }
    }

    return {
        nodes: viewNodes,
        edges: viewEdges,
    };
};

export const copyRawPipelineGraphViewData = (
    graphNodes: PipelineGraphNodeEntity[] | undefined,
    graphEdges: PipelineGraphEdgeEntity[] | undefined,
    copyId = false
): RawPipelineGraphViewData => {
    const viewData = copyPipelineGraphViewData(graphNodes, graphEdges, copyId);

    return {
        nodes: toGraphViewNodes(viewData.nodes),
        edges: toGraphViewEdges(viewData.edges),
    };
};
